import { useQuery } from '@tanstack/react-query';
import { useIsEventbritePlusEnabled } from '@/bsp/config';

const STALE_MS = 5 * 60 * 1000;

type SubscriptionResponse = {
    isSubscribed?: boolean;
    hasEarlyAccessPurchaseWindows?: boolean;
};

export type UserSubscriptionLookup = {
    isSubscribed: boolean;
    hasEarlyAccessPurchaseWindows: boolean;
    isSubscriptionReady: boolean;
};

async function fetchUserSubscription(): Promise<SubscriptionResponse> {
    const response = await fetch('/home/api/user/subscription/', { credentials: 'include' });
    if (!response.ok) {
        throw new Error(`User subscription request failed with status ${response.status}`);
    }

    return (await response.json()) as SubscriptionResponse;
}

export function useUserSubscription(publicUserId: string | undefined): UserSubscriptionLookup {
    const { isEnabled: isEventbritePlusEnabled } = useIsEventbritePlusEnabled();
    // The BFF (and the member service behind it) is only ever called when the Eventbrite+ flag is on.
    const enabled = !!publicUserId && isEventbritePlusEnabled;
    const { data, isFetched } = useQuery({
        queryKey: ['user-subscription', publicUserId ?? 'anonymous'],
        queryFn: fetchUserSubscription,
        enabled,
        staleTime: STALE_MS,
        retry: false,
    });

    if (!enabled) {
        return {
            isSubscribed: false,
            hasEarlyAccessPurchaseWindows: false,
            isSubscriptionReady: true,
        };
    }

    // isFetched remains true through refetches after either a success or failure.

    return {
        isSubscribed: data?.isSubscribed ?? false,
        hasEarlyAccessPurchaseWindows: data?.hasEarlyAccessPurchaseWindows ?? false,
        isSubscriptionReady: isFetched,
    };
}
